import { Col, Divider, Flex, Form, Input, Modal, notification, Row, Typography } from 'antd';
import axios from 'axios';
import dayjs from 'mixins/dayjsConfig.js';
import {
    filterDigits,
    getLangValue,
    getRandomSecure,
    getValueFromLanguageSet,
    localize,
} from 'mixins/helpers';
import Button from 'mixins/ui/button/button';
import React, { useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import {
    COMMON_CATEGORIES,
    COMMON_CATEGORIES_TITLES,
    LIST_UNRELIABLE_CATEGORIES_NAMES,
    UNCOMMON_CATEGORIES,
    UNCOMMON_CATEGORIES_TITLES,
} from './consts/categories';
import './service-unreliable-taxpayers.scss';
import CommonCategory from './subcomponents/CommonCategory';
import UncommonCategory from './subcomponents/UncommonCategory';
import { ApiLink } from '/react4xp/mixins/api-template/api-link';
import CmsTable from '/react4xp/mixins/ui/cms-table';
import PrintForm2 from '/react4xp/mixins/ui/print-form-2';
import Recaptcha from '/react4xp/mixins/ui/recaptcha/recaptcha';

const ACTIONS_URL = `/services/isnaportal/open-api/taxpayer-data/search`;
const PRINT_URL = `/services/isnaportal/open-api/taxpayer-data/export/pdf`;
const TIME_URL = `/services/isnaportal/open-api/admin/current-time`;

const SearchForm = (props: any) => {
    const {
        // table fields
        tableName,
        tableIin,
        tableRnn,
        tableDirectorFullName,
        tableTaxpayerDataList,
        // search form fields
        formTaxPayerCode,
        formRnn,
        formTaxPayerOrgName,
        formDirectorIin,
        formLastName,
        // modal & print fields
        modalName,
        modalIin,
        modalRnn,
        modalHeadTitle,
        modalDirectorFullName,
        modalDirectorIin,
        modalDirectorRnn,
    } = props.config;

    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState<Array<any>>([]);
    const [currentTaxpayerInfo, setCurrentTaxPayerInfo] = useState<any>({});
    const [page, setPage] = useState(1);
    const [paginationSize, setPaginationSize] = useState(25);
    const captchaRef = useRef(null);

    const [docRequestTime, setDocRequestTime] = useState<string>('');

    const [form] = Form.useForm();

    const gutter_values = { xs: 8, md: 16 };

    const onFormFinishHandler = async ({ fioWrapper: _, ...values }: any) => {
        const recaptcha = captchaRef.current.getValue();

        if (recaptcha.verified) {
            setCurrentTaxPayerInfo([]);
            setResult([]);
            setPage(1);
            setIsLoading(true);

            if (values) {
                if (props.lang === 'en') {
                    values.language = 'eng';
                } else if (props.lang === 'kk') {
                    values.language = 'kaz';
                } else {
                    values.language = 'rus';
                }
            }
            Object.keys(values).forEach((key) => {
                if (values[key] === '') {
                    delete values[key];
                }
            });

            // const result = await fetch(ACTIONS_URL, {
            //     method: 'POST',
            //     headers: {
            //         'Content-Type': 'application/json',
            //     },
            //     body: JSON.stringify(values),
            // });

            const data = await axios
                .post(ACTIONS_URL, values)
                .then((res) => res.data)
                .finally(() => {
                    captchaRef.current.reset();
                    setIsLoading(false);
                });

            if (data && data.length > 0) {
                setResult(data);
            } else {
                notification.warning({
                    message: '',
                    description: localize('notification.no-data', props.translations),
                });
            }

            setIsLoading(false);

            const docRequestTimeReq = fetch(TIME_URL, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            }).then(async (response) => {
                const _curr_date_text = await response.json();
                const _curr_date = new Date(_curr_date_text);

                setDocRequestTime(dayjs(_curr_date).format('HH:mm DD.MM.YYYY'));
            });
        } else {
            captchaRef.current.setError();
        }
    };

    const generateKey = (pre: string) => {
        return `${pre}_${new Date().getMilliseconds()}_${getRandomSecure() * 1000}`;
    };

    const getTitleForCategory = (cateName: string | any) => {
        for (let i = 0; i < LIST_UNRELIABLE_CATEGORIES_NAMES.length; i++) {
            if (LIST_UNRELIABLE_CATEGORIES_NAMES[i].id == cateName) {
                return getValueFromLanguageSet(
                    LIST_UNRELIABLE_CATEGORIES_NAMES[i],
                    getLangValue(props.language)
                );
            }
        }

        return cateName;
    };

    const getBaseForCategory = (cateName: string | any) => {
        for (let i = 0; i < COMMON_CATEGORIES.length; i++) {
            if (COMMON_CATEGORIES_TITLES[i].type == cateName) {
                return getValueFromLanguageSet(
                    COMMON_CATEGORIES_TITLES[i],
                    getLangValue(props.language)
                );
            }
        }

        for (let i = 0; i < UNCOMMON_CATEGORIES.length; i++) {
            if (UNCOMMON_CATEGORIES_TITLES[i].type == cateName) {
                return UNCOMMON_CATEGORIES_TITLES[i];
            }
        }

        return 'Заголовок';
    };

    const componentRef = useRef<any>();
    const onClickPrintHandler = useReactToPrint({
        content: () => componentRef.current,
    });

    const handleCancel = () => {
        setCurrentTaxPayerInfo({});
    };

    const byPeriodTableColumns: any = [
        {
            title: `N`,
            key: 'index',
            width: '20px',
            render: (text: string, record: any, index: number) =>
                (page - 1) * paginationSize + index + 1,
        },
        {
            title: `${tableName}`,
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: `${tableIin}`,
            dataIndex: 'iin',
            key: 'iin',
        },
        {
            title: `${tableRnn}`,
            dataIndex: 'rnn',
            key: 'rnn',
        },
        {
            title: `${tableDirectorFullName}`,
            dataIndex: 'directorFullName',
            key: 'directorFullName',
        },
        {
            title: `${tableTaxpayerDataList}`,
            dataIndex: 'taxpayerDataList',
            key: 'taxpayerDataList',
            render: (taxpayerDataList) => (
                <ol>
                    {taxpayerDataList.map((item: any) => {
                        return <li>{getTitleForCategory(item?.unreliableTaxpayerDataCategory)}</li>;
                    })}
                </ol>
            ),
        },
    ];

    const clearForm = () => {
        form.resetFields();
        setResult(null);
        setIsLoading(false);
    };

    const formValues = Form.useWatch([], form);

    const conditionalFields = [
        'taxPayerCode',
        'rnn',
        'taxPayerOrgName',
        'directorIin',
        'lastName',
        'firstName',
        'middleName',
    ];

    const getConditionalRequiredRule = (field: string) => ({
        required:
            !!formValues?.[field] ||
            !conditionalFields.filter((f) => f !== field).some((f) => formValues?.[f]),
        message: '',
    });

    const getLeaderData = (currentTaxpayerInfo: any, value: string): string => {
        if (currentTaxpayerInfo.iin === currentTaxpayerInfo.directorIin) {
            return '';
        }

        return value;
    };

    const validateFIOInputs = () => {
        form.validateFields(['lastName', 'firstName']);
    };

    return (
        <div className="search-form">
            <Form
                name="SearchUnreliableTaxpayers"
                form={form}
                layout="vertical"
                onFinish={onFormFinishHandler}
                className="main__form"
            >
                <Row gutter={gutter_values}>
                    <Col
                        xs={24}
                        md={6}
                    >
                        <Form.Item
                            name="taxPayerCode"
                            label={formTaxPayerCode}
                            rules={[
                                getConditionalRequiredRule('taxPayerCode'),
                                {
                                    min: 12,
                                    message: localize(
                                        'error.iin-bin-length-12',
                                        props.translations
                                    ),
                                },
                            ]}
                        >
                            <Input
                                placeholder={localize('placeholder.iin-bin', props.translations)}
                                maxLength={12}
                                onChange={filterDigits(form, 'taxPayerCode')}
                            />
                        </Form.Item>
                    </Col>
                    <Col
                        xs={24}
                        md={6}
                    >
                        <Form.Item
                            name="rnn"
                            label={formRnn}
                            rules={[
                                getConditionalRequiredRule('rnn'),
                                {
                                    min: 12,
                                    message: localize('error.rnn-length-12', props.translations),
                                },
                            ]}
                        >
                            <Input
                                placeholder={localize('placeholder.rnn', props.translations)}
                                maxLength={12}
                                onChange={filterDigits(form, 'rnn')}
                            />
                        </Form.Item>
                    </Col>
                    <Col
                        xs={24}
                        md={6}
                    >
                        <Form.Item
                            name="taxPayerOrgName"
                            label={formTaxPayerOrgName}
                            rules={[getConditionalRequiredRule('taxPayerOrgName')]}
                        >
                            <Input
                                placeholder={localize(
                                    'placeholder.search-by-name',
                                    props.translations
                                )}
                                maxLength={1000}
                            />
                        </Form.Item>
                    </Col>
                    <Col
                        xs={24}
                        md={6}
                    >
                        <Form.Item
                            name="directorIin"
                            label={formDirectorIin}
                            rules={[
                                getConditionalRequiredRule('directorIin'),
                                {
                                    min: 12,
                                    message: localize(
                                        'error.iin-head-length-12',
                                        props.translations
                                    ),
                                },
                            ]}
                        >
                            <Input
                                placeholder={localize('placeholder.iin-head', props.translations)}
                                onChange={filterDigits(form, 'directorIin')}
                                maxLength={12}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={gutter_values}>
                    <Col
                        xs={24}
                        style={{
                            paddingBottom: 12,
                        }}
                    >
                        <Typography.Text>
                            {(!!formValues?.lastName ||
                                !!formValues?.firstName ||
                                !!formValues?.middleName ||
                                !conditionalFields
                                    .filter(
                                        (f) =>
                                            f !== 'lastName' &&
                                            f !== 'firstName' &&
                                            f !== 'middleName'
                                    )
                                    .some((f) => formValues?.[f])) && (
                                <span className={'required-asterisk'}>*</span>
                            )}
                            {formLastName}
                        </Typography.Text>
                    </Col>
                    <Col
                        xs={24}
                        md={6}
                    >
                        <Form.Item
                            name="lastName"
                            rules={[
                                {
                                    required:
                                        (!!formValues?.firstName || !!formValues?.middleName) &&
                                        !conditionalFields
                                            .filter(
                                                (f) =>
                                                    f !== 'lastName' &&
                                                    f !== 'firstName' &&
                                                    f !== 'middleName'
                                            )
                                            .some((f) => formValues?.[f]),
                                    message: localize(
                                        'error.field-is-required',
                                        props.translations
                                    ),
                                },
                            ]}
                        >
                            <Input
                                placeholder={localize('placeholder.lastname', props.translations)}
                                maxLength={100}
                                onChange={validateFIOInputs}
                            />
                        </Form.Item>
                    </Col>
                    <Col
                        xs={24}
                        md={6}
                    >
                        <Form.Item
                            name="firstName"
                            rules={[
                                {
                                    required:
                                        (!!formValues?.lastName || !!formValues?.middleName) &&
                                        !conditionalFields
                                            .filter(
                                                (f) =>
                                                    f !== 'lastName' &&
                                                    f !== 'firstName' &&
                                                    f !== 'middleName'
                                            )
                                            .some((f) => formValues?.[f]),
                                    message: localize(
                                        'error.field-is-required',
                                        props.translations
                                    ),
                                },
                            ]}
                        >
                            <Input
                                placeholder={localize('placeholder.name', props.translations)}
                                maxLength={100}
                                onChange={validateFIOInputs}
                            />
                        </Form.Item>
                    </Col>
                    <Col
                        xs={24}
                        md={6}
                    >
                        <Form.Item name="middleName">
                            <Input
                                placeholder={localize('placeholder.middlename', props.translations)}
                                maxLength={100}
                                onChange={validateFIOInputs}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Recaptcha
                    ref={captchaRef}
                    lang={props.lang}
                />

                <Row
                    gutter={[8, 40]}
                    wrap
                >
                    <Col
                        xs={24}
                        md={20}
                    >
                        <Flex
                            gap={'small'}
                            wrap
                        >
                            <Button
                                type="ghost"
                                className="search-form__btn-reset"
                                onClick={clearForm}
                            >
                                {localize('button.reset', props.translations)}
                            </Button>
                            <Button
                                type="primary"
                                color="gold"
                                className="search-form__btn-search"
                                htmlType="submit"
                                loading={isLoading}
                            >
                                {localize('button.find', props.translations)}
                            </Button>

                            {currentTaxpayerInfo && currentTaxpayerInfo.name && (
                                <Button
                                    className="search-form__btn-print"
                                    disabled={isLoading}
                                    onClick={onClickPrintHandler}
                                >
                                    {localize('button.print-form', props.translations)}
                                </Button>
                            )}
                        </Flex>
                    </Col>
                    <Col
                        xs={24}
                        md={4}
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'flex-end',
                        }}
                    >
                        <ApiLink
                            href="#"
                            translations={props.translations}
                            onClick={props.onShow}
                        />
                    </Col>
                </Row>
            </Form>

            <Divider />
            {result && result.length > 0 && (
                <CmsTable
                    columns={byPeriodTableColumns}
                    dataSource={result}
                    type="default"
                    size="large"
                    pagination={{
                        onChange(current, pageSize) {
                            setPage(current);
                            setPaginationSize(pageSize);
                        },
                        defaultPageSize: 25,
                        showSizeChanger: true,
                    }}
                    translations={props.translations}
                    rowKey={() => Date.now() + getRandomSecure() * 1000000}
                    onRow={(record) => {
                        return {
                            onClick: () => {
                                setCurrentTaxPayerInfo(record);
                            },
                        };
                    }}
                />
            )}

            <Modal
                open={currentTaxpayerInfo && Object.keys(currentTaxpayerInfo).length > 0}
                onCancel={handleCancel}
                footer={
                    <Row
                        gutter={15}
                        justify="center"
                        className="mt-3"
                    >
                        <Col>
                            <Button
                                color="gold"
                                onClick={handleCancel}
                            >
                                {localize('button.close', props.translations)}
                            </Button>
                        </Col>
                        <Col>
                            <PrintForm2
                                apiUrl={PRINT_URL}
                                params={{ language: 'rus', taxPayerCode: currentTaxpayerInfo?.iin }}
                                isPost
                                translations={props.translations}
                            />
                        </Col>
                    </Row>
                }
                width="1000px"
            >
                <div className="selected-item-details">
                    <table
                        className="mywindow"
                        style={{ borderColor: 'rgb(205,205,205)' }}
                    >
                        <tr>
                            <td> {modalName}</td>
                            <td>{currentTaxpayerInfo.name}</td>
                        </tr>
                        <tr>
                            <td> {modalIin} </td>
                            <td> {currentTaxpayerInfo.iin}</td>
                        </tr>
                        <tr>
                            <td> {modalRnn} </td>
                            <td> {currentTaxpayerInfo.rnn}</td>
                        </tr>
                        <tr>
                            <td
                                colSpan={2}
                                align="center"
                            >
                                <strong>{modalHeadTitle}</strong>
                            </td>
                        </tr>
                        <tr>
                            <td> {modalDirectorFullName} </td>
                            <td>
                                {' '}
                                {getLeaderData(
                                    currentTaxpayerInfo,
                                    currentTaxpayerInfo.directorFullName
                                )}
                            </td>
                        </tr>
                        <tr>
                            <td> {modalDirectorIin} </td>
                            <td>
                                {' '}
                                {getLeaderData(
                                    currentTaxpayerInfo,
                                    currentTaxpayerInfo.directorIin
                                )}
                            </td>
                        </tr>
                        <tr>
                            <td> {modalDirectorRnn} </td>
                            <td>
                                {' '}
                                {getLeaderData(
                                    currentTaxpayerInfo,
                                    currentTaxpayerInfo.directorRnn
                                )}
                            </td>
                        </tr>
                        {currentTaxpayerInfo.taxpayerDataList?.map((item) => {
                            if (
                                COMMON_CATEGORIES.includes(
                                    item.unreliableTaxpayerDataCategory as string
                                )
                            ) {
                                return (
                                    <CommonCategory
                                        key={generateKey(
                                            item.actNumber + ' ' + ' ' + item.taxOrgCode
                                        )}
                                        title={getBaseForCategory(
                                            item.unreliableTaxpayerDataCategory
                                        )}
                                        taxpayerData={item}
                                        props={props}
                                    />
                                );
                            } else if (
                                UNCOMMON_CATEGORIES.includes(
                                    item.unreliableTaxpayerDataCategory as string
                                )
                            ) {
                                return (
                                    <UncommonCategory
                                        key={generateKey(
                                            item.actNumber + ' ' + ' ' + item.taxOrgCode
                                        )}
                                        title={getBaseForCategory(
                                            item.unreliableTaxpayerDataCategory
                                        )}
                                        taxpayerData={item}
                                        props={props}
                                    />
                                );
                            }
                        })}
                    </table>
                </div>
            </Modal>
        </div>
    );
};
export default SearchForm;
